import React from 'react';
import { X, Expand, Loader2 } from 'lucide-react';
import { getEntityTypeColor } from './FilterPanel';

const NODE_COLORS = {
  Person: '#f97316',
  AbstractConcept: '#8b5cf6',
  ContentAtom: '#3b82f6',
  HeartbeatInsight: '#10b981',
  Primitive: '#ec4899',
  Tension: '#f59e0b',
};

const TYPE_LABELS = {
  Person: 'Person',
  AbstractConcept: 'Concept',
  ContentAtom: 'Content',
  HeartbeatInsight: 'Insight',
  Primitive: 'Primitive',
  Tension: 'Tension',
};

// Properties to hide from the detail view (internal/redundant)
const HIDDEN_PROPS = new Set(['tenantId', 'displayName', 'label', 'name', 'axis']);

export default function NodeDetailPanel({ node, nodeDetail, nodeDetailLoading, edges, onClose, onExpand, isExpanded, expanding }) {
  if (!node) return null;

  const color = NODE_COLORS[node.nodeType] || getEntityTypeColor(node.nodeType);
  const typeLabel = TYPE_LABELS[node.nodeType] || node.nodeType;
  const isEntity = nodeDetail?.isEntity || false;
  const provenance = nodeDetail?.provenance || null;

  // Count edges by type
  const edgeCounts = {};
  (edges || []).forEach((e) => {
    const t = e.type || 'CONNECTED';
    edgeCounts[t] = (edgeCounts[t] || 0) + 1;
  });

  // Get the full title text (untruncated)
  const fullTitle = nodeDetail?.properties?.label
    || nodeDetail?.properties?.displayName
    || nodeDetail?.properties?.name
    || nodeDetail?.properties?.snippet
    || node.label;

  // Filter properties for display
  const props = nodeDetail?.properties || {};
  const displayProps = Object.entries(props).filter(([key]) => !HIDDEN_PROPS.has(key));

  return (
    <div className="w-72 shrink-0 border-l bg-card/50 flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between p-3 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{node.label}</p>
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{typeLabel}</p>
              {provenance === 'app-declared' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-medium">
                  Declared
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Actions */}
      <div className="p-3 border-b flex gap-2">
        <button
          onClick={() => onExpand(node.id)}
          disabled={isExpanded || expanding}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md hover:bg-accent/50 transition-colors disabled:opacity-50 text-foreground"
        >
          <Expand className="h-3 w-3" />
          {expanding ? 'Loading...' : isExpanded ? 'Expanded' : 'Expand'}
        </button>
      </div>

      {/* Full Title — show when title is longer than the truncated header can display */}
      {fullTitle && fullTitle.length > 28 && (
        <div className="px-3 pt-3 pb-1 border-b">
          <p className="text-xs text-foreground break-words whitespace-pre-wrap leading-relaxed">{fullTitle}</p>
        </div>
      )}

      {/* Entity ID (for app-declared entities) */}
      {isEntity && nodeDetail?.entityId && (
        <div className="px-3 pt-2 pb-1 border-b">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-muted-foreground font-mono text-[10px]">entityId</span>
            <span className="ml-auto text-foreground font-mono text-[10px] truncate max-w-[140px]">{nodeDetail.entityId}</span>
          </div>
          {nodeDetail.properties?.scope && (
            <div className="flex items-center gap-1.5 text-xs mt-1">
              <span className="text-muted-foreground font-mono text-[10px]">scope</span>
              <span className="ml-auto text-foreground font-mono text-[10px] truncate max-w-[140px]">{nodeDetail.properties.scope}</span>
            </div>
          )}
        </div>
      )}

      {/* Properties */}
      <div className="px-3 pt-3 pb-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
          Properties
        </p>
        {nodeDetailLoading ? (
          <div className="flex items-center gap-2 py-2 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span className="text-xs">Loading...</span>
          </div>
        ) : displayProps.length > 0 ? (
          <div className="space-y-1.5">
            {displayProps.map(([key, value]) => (
              <div key={key} className="text-xs">
                <span className="text-muted-foreground font-mono text-[10px]">{key}</span>
                <p className="text-foreground mt-0.5 break-words">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No additional properties</p>
        )}
      </div>

      {/* Relationships */}
      {Object.keys(edgeCounts).length > 0 && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Relationships ({edges?.length || 0})
          </p>
          <div className="space-y-1">
            {Object.entries(edgeCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-muted-foreground font-mono">{type}</span>
                  <span className="text-foreground font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Footer — Node ID */}
      <div className="p-3 border-t">
        <p className="text-[10px] text-muted-foreground font-mono truncate">{node.id}</p>
      </div>
    </div>
  );
}
